import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Drawer,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Chip,
  useTheme,
  Divider,
  CircularProgress,
  Alert,
  Avatar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import HotelIcon from '@mui/icons-material/Hotel';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import DatePicker from '../../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import moment from 'moment';
import { getRatesByRatePlan, getHotelRatePlans } from '../../services/api';
import AlertDialog from '../common/AlertDialog';
import RoomPickerGridCard from '../booking/RoomPickerGridCard';
import WalkInRoomGroupViewControl from '../booking/WalkInRoomGroupViewControl';
import DashboardWorkspaceCard from '../dashboard/DashboardWorkspaceCard';
import { buildWalkInDisplayGroups, resolveWalkInRoomTypeGroup, resolveWalkInRoomTypeImage, resolveWalkInGroupImage } from '../../utils/walkInRoomGrouping';
import {
  isWalkInAwarenessHold,
  isWalkInDirtyRoom,
  isWalkInWebCheckInHeld,
} from '../../utils/walkInHousekeeping';
import { getDashboardWorkspaceScrollSx } from '../../utils/dashboardWorkspaceLayout';
import { isMobileShellLayout } from '../../utils/mobileShellLayout';
import {
  getWalkInNightsControlRowSx,
  getWalkInNightsSelectorSx,
  getWalkInRoomGridItemProps,
  getWalkInRoomGridSpacing,
  getWalkInStepSectionHeaderSx,
  isWalkInFlowLayout,
} from '../../utils/walkInMobileLayout';
import { getHotelDateFormat, formatHotelDate } from '../../utils/dateUtils';
import {
  getRoomPickerAvailabilityHintSx,
  getRoomPickerAvailabilityTextSx,
  getRoomPickerGridWrapSx,
  getRoomPickerPageTitleSx,
  getRoomPickerStatChipSx,
  getRoomPickerTypeHeaderSx,
  getRoomPickerTypeNameSx,
  getRoomPickerTypeSectionSx,
  getRoomPickerRoomImageSx,
  ROOM_PICKER_ACCENT,
} from '../../utils/roomPickerLayout';
import { getBookingDrawerSelectMenuProps } from '../../utils/bookingDrawerLayout';
import { Z_INDEX } from '../../shared/constants/theme';

const CUSTOM_BORDER_COLOR = '#4ce1b6';

const toPositiveInt = (value, fallback = 0) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  const normalized = Math.floor(parsed);
  return normalized > 0 ? normalized : fallback;
};

const resolveRoomTypeLimits = (roomTypeMeta = {}) => {
  const baseAdults = toPositiveInt(roomTypeMeta.base_adults, 1);
  const baseChildren = toPositiveInt(roomTypeMeta.base_children, 0);
  const capacity = toPositiveInt(roomTypeMeta.capacity, 0);
  const maxGuests = toPositiveInt(
    roomTypeMeta.max_guests,
    capacity > 0 ? capacity : Math.max(1, baseAdults + baseChildren)
  );

  return { baseAdults, baseChildren, maxGuests };
};

const RoomsStep = ({
  checkInDate,
  checkOutDate,
  onCheckInDateChange,
  onCheckOutDateChange,
  nights,
  selectedRooms,
  allRoomsData,
  loadingRooms,
  onAddRoom,
  onRemoveRoom,
  roomDrawerOpen,
  onRoomDrawerOpen,
  onRoomDrawerClose,
  roomFormData,
  onRoomFormDataChange,
  bookingType,
  isWalkIn = false,
  selectedDays = 1,
  onDaysChange,
  groupView = 'room_type',
  onGroupViewChange,
  themeMode = 'dark',
  onDirtyRoomHousekeeping,
}) => {
  const theme = useTheme();
  const isMobileLayout = isMobileShellLayout();
  const useWalkInFlowLayout = isWalkInFlowLayout();
  const useCompactRoomGrid = isMobileLayout || useWalkInFlowLayout;
  const isDark = theme.palette.mode === 'dark';
  const formatCurrency = (value) => `₹${value.toFixed(2)}`;
  const roomDrawerSurface = isDark ? '#2a2a31' : '#ffffff';
  const mealPlanMenuProps = useMemo(
    () => getBookingDrawerSelectMenuProps(isDark, roomDrawerSurface),
    [isDark, roomDrawerSurface],
  );

  // Alert dialog state
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertTitle, setAlertTitle] = useState('Alert');
  const [alertSeverity, setAlertSeverity] = useState('warning');
  
  // Show alert dialog
  const showAlert = (message, title = 'Alert', severity = 'warning') => {
    setAlertMessage(message);
    setAlertTitle(title);
    setAlertSeverity(severity);
    setAlertDialogOpen(true);
  };

  const handleCloseAlert = () => {
    setAlertDialogOpen(false);
  };

  // Validation error shown inside the room details drawer (instead of global popup)
  const [drawerValidationError, setDrawerValidationError] = useState('');
  useEffect(() => {
    if (!roomDrawerOpen) setDrawerValidationError('');
  }, [roomDrawerOpen]);
  
  // Datepicker state for room availability filtering
  const [selectedDate, setSelectedDate] = useState(checkInDate || moment());
  
  // State for expanded room types (accordion functionality)
  const [expandedRoomTypes, setExpandedRoomTypes] = useState(new Set());
  
  // Ensure first room type is expanded by default when filtered data is available
  useEffect(() => {
    if (filteredRoomsData && filteredRoomsData.length > 0) {
      const firstRoomTypeId = String(filteredRoomsData[0].room_type.id);
      setExpandedRoomTypes((prev) => {
        // Always ensure first room type is expanded
        if (!prev.has(firstRoomTypeId)) {
          const newSet = new Set(prev);
          newSet.add(firstRoomTypeId);
          return newSet;
        }
        return prev;
      });
    }
  }, [filteredRoomsData]);
  
  // Also initialize when allRoomsData first loads
  useEffect(() => {
    if (allRoomsData?.room_types && allRoomsData.room_types.length > 0 && expandedRoomTypes.size === 0) {
      const firstRoomTypeId = String(allRoomsData.room_types[0].room_type.id);
      setExpandedRoomTypes(new Set([firstRoomTypeId]));
    }
  }, [allRoomsData]);
  
  // Toggle room type expand/collapse
  const toggleRoomType = (roomTypeId) => {
    const newExpanded = new Set(expandedRoomTypes);
    const roomTypeIdStr = String(roomTypeId);
    if (newExpanded.has(roomTypeIdStr)) {
      newExpanded.delete(roomTypeIdStr);
    } else {
      newExpanded.add(roomTypeIdStr);
    }
    setExpandedRoomTypes(newExpanded);
  };
  
  // Calculate checkout date based on selected date and days
  const calculateCheckoutDate = (checkIn, days) => {
    return moment(checkIn).add(days || 1, 'days');
  };
  
  // Filter rooms: reservations — only is_available. Walk-in — also show checkout-morning / overdue
  // holds (disabled) so staff sees "Pending checkout" instead of a missing room.
  // When inventory is already consumed by bookings (including unallocated), do not offer free-looking
  // rooms for selection; only show held rooms for awareness.
  const filterRoomsByAvailability = (roomTypeGroups) => {
    if (!roomTypeGroups) return [];

    return roomTypeGroups.map((roomTypeGroup) => {
      const maxAvailable = roomTypeGroup.max_available_rooms ?? 0;
      const sourceRooms = roomTypeGroup.rooms || [];
      let visibleRooms;
      if (isWalkIn) {
        const heldRooms = sourceRooms.filter((room) => isWalkInAwarenessHold(room));
        const freeRooms = sourceRooms.filter((room) => room.is_available === true);
        // Inventory slot exists -> show free rooms (pick any) + held rooms.
        // No inventory left -> show only held rooms (disabled), hide free-looking rooms.
        visibleRooms = maxAvailable > 0
          ? [...freeRooms, ...heldRooms.filter((room) => !freeRooms.some((free) => free.id === room.id))]
          : heldRooms;
      } else {
        visibleRooms = sourceRooms.filter((room) => room.is_available === true);
      }
      const selectableCount = visibleRooms.filter((room) => room.is_available === true).length;
      const isDisabled = isWalkIn
        ? maxAvailable === 0 && selectableCount === 0
        : maxAvailable === 0 || selectableCount === 0;

      return {
        ...roomTypeGroup,
        rooms: visibleRooms,
        blocked_rooms: roomTypeGroup.blocked_rooms || 0,
        block_details: roomTypeGroup.block_details || [],
        booked_rooms: roomTypeGroup.booked_rooms ?? 0,
        isDisabled, // No inventory / rooms available for check-in
      };
    });
    // Show all room types including disabled (0 available) so user sees "1 booked, 1 blocked" etc.
  };
  
  const filteredRoomsData = filterRoomsByAvailability(allRoomsData?.room_types);
  const displayGroups = useMemo(
    () => (isWalkIn ? buildWalkInDisplayGroups(filteredRoomsData, groupView) : filteredRoomsData.map((group) => ({
      key: String(group.room_type.id),
      label: group.room_type.name,
      roomTypeGroup: group,
      rooms: group.rooms || [],
      total_rooms: group.total_rooms ?? 0,
      booked_rooms: group.booked_rooms ?? 0,
      blocked_rooms: group.blocked_rooms ?? 0,
      max_available_rooms: group.max_available_rooms ?? 0,
      isDisabled: group.isDisabled === true,
    }))),
    [filteredRoomsData, groupView, isWalkIn]
  );
  
  const handleDateChange = (newDate) => {
    if (newDate && moment.isMoment(newDate)) {
      setSelectedDate(newDate);
      // Update check-in date when datepicker changes
      onCheckInDateChange(newDate);
      // Update checkout date based on current check-out or default to 1 day for walk-in
      const currentDays = checkOutDate && checkInDate ? checkOutDate.diff(checkInDate, 'days') : 1;
      const newCheckOut = newDate.clone().add(currentDays, 'days');
      onCheckOutDateChange(newCheckOut);
    }
  };
  
  const handlePrevClick = () => {
    const newDate = selectedDate.clone().subtract(1, 'days');
    handleDateChange(newDate);
  };
  
  const handleNextClick = () => {
    const newDate = selectedDate.clone().add(1, 'days');
    handleDateChange(newDate);
  };
  
  const handleToday = () => {
    handleDateChange(moment());
  };
  
  // Sync selectedDate with checkInDate when it changes externally
  useEffect(() => {
    if (checkInDate && moment(checkInDate).format('YYYY-MM-DD') !== moment(selectedDate).format('YYYY-MM-DD')) {
      setSelectedDate(checkInDate);
    }
  }, [checkInDate]);

  // Calculate total hours for hourly bookings
  const calculateTotalHours = () => {
    if (bookingType === 'hourly' && checkInDate && checkOutDate) {
      const startTime = checkInDate.clone();
      const endTime = checkOutDate.clone();
      const diffInHours = endTime.diff(startTime, 'hours', true);
      return Math.max(0, diffInHours);
    }
    return 0;
  };

  const totalHours = calculateTotalHours();

  // Get selected room type from the room being edited
  const selectedRoomType = roomFormData.roomTypeId
    ? allRoomsData?.room_types?.find((rt) => {
        const rtIdStr = String(rt.room_type.id);
        const formIdStr = String(roomFormData.roomTypeId);
        return rtIdStr === formIdStr;
      })
    : null;
  const selectedRoomTypeMeta = selectedRoomType?.room_type || {};
  const selectedRoomTypeName = selectedRoomTypeMeta.name || 'Room Type';
  const selectedRoomTypeImage = selectedRoomTypeMeta.image_url || selectedRoomTypeMeta.image || '';
  const selectedRoomNumberLabel = roomFormData.roomNumber || (roomFormData.roomId ? `#${roomFormData.roomId}` : '');

  // Get room type constraints
  const {
    baseAdults,
    baseChildren,
    maxGuests,
  } = resolveRoomTypeLimits(selectedRoomType?.room_type);

  // Max totals based on 1 room (walk-in always has 1 room) and total capacity
  const maxTotalCapacity = maxGuests; // 1 room * max_guests
  const maxTotalAdults = maxTotalCapacity - (roomFormData.children || 0);
  const maxTotalChildren = maxTotalCapacity - (roomFormData.adults || 0);

  // State for rates based on meal plan
  const [mealPlanRates, setMealPlanRates] = useState(null);
  
  // State for available meal plans (rate plans) for the selected room type
  const [availableMealPlans, setAvailableMealPlans] = useState([]);

  // Get rates from selected room type for the check-in date
  const parseRate = (value) => {
    if (value === null || value === undefined) return 0;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Use rates from mealPlanRates (fetched from daily inventory) if available, otherwise fallback to room type rates
  // mealPlanRates is set to data.rates from the API response, which is the rates object itself
  const ratesToUse = mealPlanRates || selectedRoomType?.room_type || {};
  const baseRate = parseRate(ratesToUse.base_rate ?? ratesToUse.final_rate);
  const singlePersonRate = parseRate(ratesToUse.single_occupancy_rate ?? ratesToUse.base_rate);
  const extraAdultRate = parseRate(ratesToUse.extra_adult_rate);
  const extraChildRate = parseRate(ratesToUse.extra_child_rate);
  
  // Debug logging// Calculate extra adults and children beyond base capacity
  // Walk-in always has 1 room
  const baseAdultsCapacity = baseAdults; // 1 room * baseAdults
  const baseChildrenCapacity = baseChildren; // 1 room * baseChildren
  const extraAdultsCount = Math.max(0, roomFormData.adults - baseAdultsCapacity);
  const extraChildrenCount = Math.max(0, roomFormData.children - baseChildrenCapacity);

  // Refs to track if user has manually edited the values
  const userEditedAdultCharge = useRef(false);
  const userEditedChildCharge = useRef(false);
  const prevExtraAdultsCount = useRef(extraAdultsCount);
  const prevExtraChildrenCount = useRef(extraChildrenCount);
  const prevExtraAdultRate = useRef(extraAdultRate);
  const prevExtraChildRate = useRef(extraChildRate);
  const prevRoomTypeId = useRef(roomFormData.roomTypeId);

  // Reset user edited flags when room type changes
  useEffect(() => {
    if (prevRoomTypeId.current !== roomFormData.roomTypeId) {
      userEditedAdultCharge.current = false;
      userEditedChildCharge.current = false;
      prevRoomTypeId.current = roomFormData.roomTypeId;
    }
  }, [roomFormData.roomTypeId]);

  // Auto-fill extra charges when rates are available and input becomes visible
  // Always sync with default rate if user hasn't manually edited
  useEffect(() => {
    if (selectedRoomType && roomFormData.roomTypeId) {
      const updates = {};

      // Auto-fill extra adult charge when input becomes visible (extraAdultsCount > 0)
      if (extraAdultsCount > 0) {
        // Check if extraAdultsCount just became > 0 (was 0 before)
        const justBecameVisible = prevExtraAdultsCount.current === 0 && extraAdultsCount > 0;
        // Check if rate has changed
        const rateChanged = prevExtraAdultRate.current !== extraAdultRate;
        
        // Auto-fill/sync with default rate if:
        // 1. Input just became visible (extraAdultsCount changed from 0 to > 0), OR
        // 2. Rate has changed and user hasn't manually edited, OR
        // 3. Value is 0 or not set and user hasn't manually edited
        if (justBecameVisible || (rateChanged && !userEditedAdultCharge.current) || (!userEditedAdultCharge.current && (!roomFormData.extraAdultCharge || roomFormData.extraAdultCharge === 0))) {
          if (extraAdultRate > 0) {
            updates.extraAdultCharge = extraAdultRate;}
        }
      } else {
        // Reset to 0 when no extra adults
        if (roomFormData.extraAdultCharge > 0) {
          updates.extraAdultCharge = 0;
        }
        // Reset the flag when extraAdultsCount becomes 0
        userEditedAdultCharge.current = false;
      }

      // Auto-fill extra child charge when input becomes visible (extraChildrenCount > 0)
      if (extraChildrenCount > 0) {
        // Check if extraChildrenCount just became > 0 (was 0 before)
        const justBecameVisible = prevExtraChildrenCount.current === 0 && extraChildrenCount > 0;
        // Check if rate has changed
        const rateChanged = prevExtraChildRate.current !== extraChildRate;
        
        // Auto-fill/sync with default rate if:
        // 1. Input just became visible (extraChildrenCount changed from 0 to > 0), OR
        // 2. Rate has changed and user hasn't manually edited, OR
        // 3. Value is 0 or not set and user hasn't manually edited
        if (justBecameVisible || (rateChanged && !userEditedChildCharge.current) || (!userEditedChildCharge.current && (!roomFormData.extraChildCharge || roomFormData.extraChildCharge === 0))) {
          if (extraChildRate > 0) {
            updates.extraChildCharge = extraChildRate;}
        }
      } else {
        // Reset to 0 when no extra children
        if (roomFormData.extraChildCharge > 0) {
          updates.extraChildCharge = 0;
        }
        // Reset the flag when extraChildrenCount becomes 0
        userEditedChildCharge.current = false;
      }

      // Update previous values
      prevExtraAdultsCount.current = extraAdultsCount;
      prevExtraChildrenCount.current = extraChildrenCount;
      prevExtraAdultRate.current = extraAdultRate;
      prevExtraChildRate.current = extraChildRate;

      if (Object.keys(updates).length > 0) {
        onRoomFormDataChange({ ...roomFormData, ...updates });
      }
    }
  }, [
    extraAdultsCount, 
    extraChildrenCount, 
    extraAdultRate, 
    extraChildRate, 
    selectedRoomType, 
    roomFormData.roomTypeId, 
    roomFormData.adults, 
    roomFormData.children, 
    mealPlanRates
  ]);

  // Auto-fill Room Rate/Night based on adults selection and meal plan rates (walk-in always has 1 room)
  useEffect(() => {
    if (selectedRoomType && roomFormData.roomTypeId && roomFormData.adults > 0) {
      // Use rates from meal plan if available, otherwise use room type rates
      const currentRates = mealPlanRates || selectedRoomType?.room_type || {};
      const currentBaseRate = parseRate(currentRates.base_rate ?? currentRates.final_rate);
      const currentSingleRate = parseRate(currentRates.single_occupancy_rate ?? currentRates.base_rate);
      
      // Walk-in always has 1 room, so adultsPerRoom = adults
      const adultsPerRoom = roomFormData.adults;
      let rateToUse = 0;
      if (adultsPerRoom === 1) {
        rateToUse = currentSingleRate;
      } else if (adultsPerRoom > 1) {
        rateToUse = currentBaseRate;
      }

      const currentRate = roomFormData.ratePerNight || 0;
      // When meal plan changes, always update the rate if we have a valid rate
      // Otherwise, only update if rate is 0 or matches a known rate
      const matchesSingleRate = Math.abs(currentRate - currentSingleRate) < 0.01;
      const matchesBaseRate = Math.abs(currentRate - currentBaseRate) < 0.01;
      const needsUpdate = rateToUse > 0 && Math.abs(currentRate - rateToUse) > 0.01;
      // Always update when meal plan rates change, or if rate is 0, or if current rate matches old rate
      const shouldAutoFill = currentRate === 0 || needsUpdate || matchesSingleRate || matchesBaseRate;

      if (rateToUse > 0 && shouldAutoFill) {
        onRoomFormDataChange({ ...roomFormData, ratePerNight: rateToUse });
      }
    }
  }, [selectedRoomType, roomFormData.roomTypeId, roomFormData.roomId, roomFormData.adults, mealPlanRates]);

  // Fetch available meal plans (rate plans) for the hotel on component mount
  useEffect(() => {
    const params = {};
    if (checkInDate) params.check_in_date = checkInDate.format('YYYY-MM-DD');
    if (checkOutDate) params.check_out_date = checkOutDate.format('YYYY-MM-DD');
    if (roomFormData.roomTypeId) params.room_type_id = roomFormData.roomTypeId;
    // Fetch all rate plans configured for the hotel
    getHotelRatePlans({ params })
      .then((data) => {
        // Handle different possible response structures
        const ratePlans = data?.rate_plans || data?.ratePlans || data || [];
        if (Array.isArray(ratePlans) && ratePlans.length > 0) {
          // Map rate plans to meal plan format
          const mealPlans = ratePlans.map((rp) => ({
            value: rp.code,
            label: rp.name || rp.code,
            id: rp.id,
            meal_plan: rp.meal_plan,
          }));
          setAvailableMealPlans(mealPlans);
          
          const selectedStillAvailable = mealPlans.some((plan) => plan.value === roomFormData.mealPlan);
          if (!roomFormData.mealPlan || !selectedStillAvailable) {
            onRoomFormDataChange({ ...roomFormData, mealPlan: mealPlans[0].value });
          }
        } else {
          // No rate plans available for the hotel
          setAvailableMealPlans([]);
          if (roomFormData.mealPlan) {
            onRoomFormDataChange({ ...roomFormData, mealPlan: '' });
          }
        }
      })
      .catch((error) => {
        // No meal plans available on error
        setAvailableMealPlans([]);
      });
  }, [checkInDate, checkOutDate, roomFormData.roomTypeId]); // Refresh when room/date changes for stop-sell filtering

  // Auto-select first rate plan when drawer opens and no rate plan is selected
  useEffect(() => {
    if (roomDrawerOpen && availableMealPlans.length > 0 && !roomFormData.mealPlan) {onRoomFormDataChange({ ...roomFormData, mealPlan: availableMealPlans[0].value });
    }
  }, [roomDrawerOpen, availableMealPlans]);

  // Fetch rates from daily inventory when rate plan, room type, or check-in date changes
  useEffect(() => {
    if (roomFormData.roomTypeId && roomFormData.mealPlan && checkInDate) {
      // roomFormData.mealPlan contains the rate plan code
      getRatesByRatePlan(
        roomFormData.roomTypeId,
        checkInDate.format('YYYY-MM-DD'),
        roomFormData.mealPlan,
        checkOutDate?.format('YYYY-MM-DD')
      )
        .then((data) => {if (data.rates) {
            setMealPlanRates(data.rates);
          } else {
            setMealPlanRates(null);
          }
        })
        .catch((error) => {setMealPlanRates(null);
        });
    } else {
      setMealPlanRates(null);
    }
  }, [roomFormData.roomTypeId, roomFormData.roomId, roomFormData.mealPlan, checkInDate, checkOutDate]);

  const handleNumberChange = (field, delta) => {
    const current = roomFormData[field] || 0;
    let newValue = Math.max(0, current + delta);

    if (field === 'adults') {
      // Ensure we have valid maxTotalAdults (not NaN)
      const validMaxAdults = isNaN(maxTotalAdults) ? maxGuests : maxTotalAdults;
      newValue = Math.min(newValue, validMaxAdults);
      const validMaxCapacity = isNaN(maxTotalCapacity) ? maxGuests : maxTotalCapacity;
      const newChildren = Math.min(roomFormData.children || 0, validMaxCapacity - newValue);
      onRoomFormDataChange({
        ...roomFormData,
        adults: newValue,
        children: newChildren,
      });
      return;
    } else if (field === 'children') {
      // Ensure we have valid maxTotalChildren (not NaN)
      const validMaxChildren = isNaN(maxTotalChildren) ? maxGuests : maxTotalChildren;
      newValue = Math.min(newValue, validMaxChildren);
      const validMaxCapacity = isNaN(maxTotalCapacity) ? maxGuests : maxTotalCapacity;
      const newAdults = Math.min(roomFormData.adults || 0, validMaxCapacity - newValue);
      onRoomFormDataChange({
        ...roomFormData,
        adults: newAdults,
        children: newValue,
      });
      return;
    }

    onRoomFormDataChange({ ...roomFormData, [field]: newValue });
  };

  const handleAddRoom = () => {
    setDrawerValidationError('');
    if (!roomFormData.roomTypeId || !roomFormData.roomId) {
      setDrawerValidationError('Please select a room');
      return;
    }
    // For walk-in: use selectedDays directly (nights from days selection)
    // For reservations: calculate days from check-in and check-out dates
    let effectiveDays;
    if (isWalkIn) {
      // Walk-in: use selectedDays (nights) directly
      effectiveDays = selectedDays || 1;
      if (effectiveDays < 1) {
        setDrawerValidationError('Please select at least 1 night');
        return;
      }
    } else {
      // Reservation: calculate days from check-in and check-out dates
      const days = checkOutDate && checkInDate ? checkOutDate.diff(checkInDate, 'days') : 1;
      if (days < 1) {
        setDrawerValidationError('Check-out date must be after check-in date');
        return;
      }
      effectiveDays = Math.max(1, Math.min(30, days));
    }
    
    if (roomFormData.adults === 0 && roomFormData.children === 0) {
      setDrawerValidationError('Please select at least one adult or child');
      return;
    }
    if (roomFormData.ratePerNight === 0) {
      setDrawerValidationError('Please enter room rate per night');
      return;
    }

    const selectedRoomType = allRoomsData?.room_types?.find((rt) => {
      const rtIdStr = String(rt.room_type.id);
      const formIdStr = String(roomFormData.roomTypeId);
      return rtIdStr === formIdStr;
    });

    const {
      baseAdults,
      baseChildren,
    } = resolveRoomTypeLimits(selectedRoomType?.room_type);
    // For walk-in, we have 1 room, so base capacity is just base_adults and base_children
    const baseAdultsCapacity = baseAdults;
    const baseChildrenCapacity = baseChildren;
    const extraAdultsCount = Math.max(0, roomFormData.adults - baseAdultsCapacity);
    const extraChildrenCount = Math.max(0, roomFormData.children - baseChildrenCapacity);

    // Use default rates if user hasn't edited the values
    // If roomFormData.extraAdultCharge is 0 or not set, use the default rate from extraAdultRate
    // extraAdultRate and extraChildRate are defined at component level and available here
    const effectiveExtraAdultRate = (roomFormData.extraAdultCharge && roomFormData.extraAdultCharge > 0) 
      ? roomFormData.extraAdultCharge 
      : (extraAdultRate || 0);
    const effectiveExtraChildRate = (roomFormData.extraChildCharge && roomFormData.extraChildCharge > 0) 
      ? roomFormData.extraChildCharge 
      : (extraChildRate || 0);

    // For walk-in: 1 room, so basePrice = ratePerNight * days
    const basePrice = roomFormData.ratePerNight * effectiveDays;
    const totalExtraAdultCharge = effectiveExtraAdultRate * extraAdultsCount * effectiveDays;
    const totalExtraChildCharge = effectiveExtraChildRate * extraChildrenCount * effectiveDays;
    const extraCharges = totalExtraAdultCharge + totalExtraChildCharge;
    const discount = 0;
    const priceAfterDiscount = basePrice + extraCharges - discount;

    const prices = {
      basePrice,
      extraAdultCharge: totalExtraAdultCharge,
      extraChildCharge: totalExtraChildCharge,
      discount,
      priceAfterDiscount,
      total: priceAfterDiscount,
    };

    const newRoom = {
      roomType: selectedRoomType?.room_type?.name || '',
      roomTypeId: roomFormData.roomTypeId,
      roomId: roomFormData.roomId,
      roomNumber: roomFormData.roomNumber,
      mealPlan: roomFormData.mealPlan,
      rooms: 1, // Walk-in always has 1 room
      days: effectiveDays, // For walk-in: selectedDays (nights), For reservation: calculated from dates
      adults: roomFormData.adults,
      children: roomFormData.children,
      ratePerNight: roomFormData.ratePerNight,
      extraAdultRate: effectiveExtraAdultRate, // Rate per extra adult per day (default or user-edited)
      extraChildRate: effectiveExtraChildRate, // Rate per extra child per day (default or user-edited)
      extraAdultsCount,
      extraChildrenCount,
      baseAdults,
      baseChildren,
      checkInDate: checkInDate.format('YYYY-MM-DD'),
      checkOutDate: checkOutDate.format('YYYY-MM-DD'),
      ...prices, // This will add basePrice, extraAdultCharge (total), extraChildCharge (total), etc.
    };

    onAddRoom(newRoom);
    onRoomDrawerClose();
    onRoomFormDataChange({
      roomTypeId: '',
      roomId: '',
      roomNumber: '',
      mealPlan: 'EP',
      adults: 0,
      children: 0,
      ratePerNight: 0,
      extraAdultCharge: 0,
      extraChildCharge: 0,
    });
  };

  const walkInNightsToolbar = isWalkIn && onDaysChange ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1,
        py: 0.35,
        borderRadius: '10px',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        minHeight: 36,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: isDark ? 'rgba(248,250,252,0.72)' : '#64748b',
          fontWeight: 700,
          fontSize: '0.68rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          whiteSpace: 'nowrap',
        }}
      >
        Nights
      </Typography>
      <Box sx={{ ...getWalkInNightsControlRowSx(), gap: 0.35 }}>
        <IconButton
          size="small"
          onClick={() => onDaysChange(Math.max(1, selectedDays - 1))}
          disabled={selectedDays <= 1}
          sx={{
            border: `1px solid ${CUSTOM_BORDER_COLOR}`,
            color: CUSTOM_BORDER_COLOR,
            width: 28,
            height: 28,
            '&:hover': { backgroundColor: 'rgba(76, 225, 182, 0.1)' },
            '&:disabled': {
              border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              color: isDark ? '#666' : '#999',
            },
          }}
        >
          <RemoveIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Typography
          variant="body2"
          sx={{
            minWidth: 24,
            textAlign: 'center',
            color: CUSTOM_BORDER_COLOR,
            fontWeight: 800,
            fontSize: '0.95rem',
          }}
        >
          {selectedDays}
        </Typography>
        <IconButton
          size="small"
          onClick={() => onDaysChange(Math.min(30, selectedDays + 1))}
          disabled={selectedDays >= 30}
          sx={{
            border: `1px solid ${CUSTOM_BORDER_COLOR}`,
            color: CUSTOM_BORDER_COLOR,
            width: 28,
            height: 28,
            '&:hover': { backgroundColor: 'rgba(76, 225, 182, 0.1)' },
            '&:disabled': {
              border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              color: isDark ? '#666' : '#999',
            },
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
      {checkInDate && checkOutDate ? (
        <Typography
          variant="caption"
          sx={{
            color: isDark ? 'rgba(248,250,252,0.55)' : '#64748b',
            fontSize: '0.68rem',
            whiteSpace: 'nowrap',
          }}
        >
          {checkInDate.format('DD MMM')} – {checkOutDate.format('DD MMM')}
        </Typography>
      ) : null}
    </Box>
  ) : null;

  const walkInHeaderActions = isWalkIn && onGroupViewChange ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      <WalkInRoomGroupViewControl
        groupView={groupView}
        onGroupViewChange={onGroupViewChange}
        themeMode={themeMode}
      />
      {walkInNightsToolbar}
    </Box>
  ) : null;

  const renderReservationHeader = () => (
    <Box sx={getWalkInStepSectionHeaderSx(isMobileLayout)}>
      <Typography variant="h5" sx={getRoomPickerPageTitleSx(isDark)}>
        Select Rooms
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton
          onClick={handlePrevClick}
          sx={{
            borderRadius: '4px',
            width: '36px',
            height: '36px',
            padding: 0,
            color: isDark ? '#ddd' : '#333',
            '&:hover': { backgroundColor: isDark ? '#38373f' : '#e0e0e0' },
          }}
        >
          <ChevronLeft />
        </IconButton>
        <Box sx={{ minWidth: '200px' }}>
          <DatePicker
            value={selectedDate}
            onChange={handleDateChange}
            format={getHotelDateFormat()}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                sx: {
                  '& .MuiOutlinedInput-root': {
                    height: '36px',
                    fontSize: '14px',
                    '& fieldset': { borderColor: CUSTOM_BORDER_COLOR },
                    '&:hover fieldset': { borderColor: CUSTOM_BORDER_COLOR },
                    '&.Mui-focused fieldset': { borderColor: CUSTOM_BORDER_COLOR },
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 12px',
                    textAlign: 'center',
                    color: isDark ? '#ddd' : '#333',
                  },
                },
              },
            }}
          />
        </Box>
        <IconButton
          onClick={handleNextClick}
          sx={{
            borderRadius: '4px',
            width: '36px',
            height: '36px',
            padding: 0,
            color: isDark ? '#ddd' : '#333',
            '&:hover': { backgroundColor: isDark ? '#38373f' : '#e0e0e0' },
          }}
        >
          <ChevronRight />
        </IconButton>
      </Box>
    </Box>
  );

  const renderWalkInMobileHeader = () => (
    <Box sx={getWalkInStepSectionHeaderSx(useCompactRoomGrid)}>
      <Typography variant="h5" sx={getRoomPickerPageTitleSx(isDark)}>
        Select Rooms
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1, width: '100%' }}>
        {onGroupViewChange ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <WalkInRoomGroupViewControl
              groupView={groupView}
              onGroupViewChange={onGroupViewChange}
              themeMode={themeMode}
            />
          </Box>
        ) : null}
        {walkInNightsToolbar ? (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>{walkInNightsToolbar}</Box>
        ) : null}
      </Box>
    </Box>
  );

  const renderRoomGroups = () => (
    loadingRooms ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    ) : displayGroups && displayGroups.length > 0 ? (
      <Box>
        {displayGroups.map((displayGroup) => {
          const isDisabled = displayGroup.isDisabled === true;
          const hasInformationalRooms =
            isWalkIn &&
            displayGroup.rooms?.some((room) => isWalkInAwarenessHold(room));
          const total = displayGroup.total_rooms ?? 0;
          const booked = displayGroup.booked_rooms ?? 0;
          const blocked = displayGroup.blocked_rooms ?? 0;
          const available = displayGroup.max_available_rooms ?? Math.max(0, total - booked - blocked);
          const parts = [];
          if (booked > 0) parts.push(`${booked} booked`);
          if (blocked > 0) parts.push(`${blocked} blocked`);
          const suffix = parts.length > 0 ? ` · ${parts.join(' and ')}` : '';
          const isDisabledMsg = isDisabled;
          const message = isDisabledMsg
            ? `No rooms available — all booked or blocked.${suffix}`
            : available === 1
              ? `You have 1 room available — choose it.${suffix}`
              : `You have ${available} rooms available — choose any one.${suffix}`;

          return (
            <Box
              key={displayGroup.key}
              sx={{
                ...getRoomPickerTypeSectionSx(isDark, { allowOverflow: useWalkInFlowLayout }),
                opacity: isDisabled ? 0.65 : 1,
                pointerEvents: isDisabled && !hasInformationalRooms ? 'none' : 'auto',
              }}
            >
              <Box
                sx={{
                  ...getRoomPickerTypeHeaderSx(isDark),
                  ...(isWalkIn ? { alignItems: 'center' } : {}),
                }}
              >
                <Box
                  sx={{
                    minWidth: 0,
                    flex: 1,
                    ...(isWalkIn
                      ? {
                          display: 'flex',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: { xs: 0.75, sm: 1 },
                        }
                      : {}),
                  }}
                >
                  <Typography sx={getRoomPickerTypeNameSx(isDark)}>
                    {displayGroup.label}
                  </Typography>
                  <Box
                    sx={{
                      ...getRoomPickerAvailabilityHintSx(isDark, { disabled: isDisabledMsg }),
                      ...(isWalkIn ? {} : { mt: 0.75 }),
                    }}
                  >
                    <MeetingRoomIcon sx={{ fontSize: 16, color: isDisabledMsg ? '#94a3b8' : ROOM_PICKER_ACCENT, flexShrink: 0 }} />
                    <Typography sx={getRoomPickerAvailabilityTextSx(isDark, { disabled: isDisabledMsg })}>
                      {message}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
                  {blocked > 0 ? (
                    <Chip label={`${blocked} blocked`} size="small" sx={getRoomPickerStatChipSx(isDark, 'blocked')} />
                  ) : null}
                  <Chip label={`${available} available`} size="small" sx={getRoomPickerStatChipSx(isDark, 'available')} />
                  {booked > 0 ? (
                    <Chip label={`${booked} booked`} size="small" sx={getRoomPickerStatChipSx(isDark, 'booked')} />
                  ) : null}
                  {(() => {
                    const groupImageUrl = resolveWalkInGroupImage(displayGroup);
                    if (!groupImageUrl) return null;
                    return (
                      <Avatar
                        src={groupImageUrl}
                        alt=""
                        sx={{
                          ...getRoomPickerRoomImageSx(isMobileLayout, isDark),
                          width: 44,
                          height: 44,
                        }}
                      />
                    );
                  })()}
                </Box>
              </Box>

              <Box sx={getRoomPickerGridWrapSx()}>
                <Grid container spacing={getWalkInRoomGridSpacing(useCompactRoomGrid)}>
                  {displayGroup.rooms.map((room) => {
                    const roomTypeGroup =
                      displayGroup.roomTypeGroup || resolveWalkInRoomTypeGroup(filteredRoomsData, room);
                    const isSelected = selectedRooms.some((sr) => sr.roomId === room.id);
                    const maintenanceBlocked = room.is_maintenance_blocked === true;
                    const webCheckInHeld = isWalkIn && isWalkInWebCheckInHeld(room);
                    const walkInHeld =
                      maintenanceBlocked ||
                      webCheckInHeld ||
                      (isWalkIn &&
                        room.is_available === false &&
                        (room.walk_in_pending_checkout === true || room.walk_in_overdue_checkout === true));
                    const maxAvailable = roomTypeGroup?.max_available_rooms || 0;
                    const selectedRoomsOfSameType = roomTypeGroup
                      ? selectedRooms.filter((sr) => sr.roomTypeId === roomTypeGroup.room_type.id).length
                      : 0;
                    const inventoryLimitReached =
                      isWalkIn
                      && !isSelected
                      && room.is_available === true
                      && maxAvailable > 0
                      && selectedRoomsOfSameType >= maxAvailable;

                    return (
                      <Grid item {...getWalkInRoomGridItemProps(useCompactRoomGrid)} key={room.id}>
                        <RoomPickerGridCard
                          room={room}
                          isDark={isDark}
                          isMobile={useCompactRoomGrid}
                          isSelected={isSelected}
                          isDisabled={inventoryLimitReached}
                          walkInHeld={walkInHeld}
                          heldKind={
                            maintenanceBlocked
                              ? 'maintenance'
                              : webCheckInHeld
                                ? 'web_checkin'
                                : room.walk_in_overdue_checkout
                                  ? 'overdue_checkout'
                                  : 'pending_checkout'
                          }
                          heldLabel={
                            maintenanceBlocked
                              ? 'Under maintenance'
                              : webCheckInHeld
                                ? 'Web check-in'
                                : room.walk_in_overdue_checkout
                                  ? 'Overdue checkout'
                                  : 'Pending checkout'
                          }
                          heldTooltip={
                            maintenanceBlocked
                              ? 'This room is blocked for maintenance and cannot be selected.'
                              : webCheckInHeld
                                ? 'This room has an active web check-in invite. Wait until the link expires or cancel the invite before assigning it to a walk-in.'
                                : room.walk_in_overdue_checkout
                                  ? 'The previous guest is overdue for checkout. Complete checkout before selecting this room.'
                                  : walkInHeld
                                    ? 'The previous guest is scheduled to check out today. The room is shown for awareness but cannot be selected until checkout is complete.'
                                    : inventoryLimitReached
                                      ? `Only ${maxAvailable} room(s) left in inventory for this type. Deselect another room to choose this one.`
                                      : ''
                          }
                          imageUrl={resolveWalkInRoomTypeImage(filteredRoomsData, displayGroup, room)}
                          onClick={() => {
                            if (inventoryLimitReached) {
                              showAlert(
                                `Only ${maxAvailable} room(s) available in inventory for "${roomTypeGroup?.room_type?.name || 'this type'}". Deselect another room to choose this one.`,
                                'Inventory limit reached',
                                'warning',
                              );
                              return;
                            }
                            if (walkInHeld) {
                              showAlert(
                                maintenanceBlocked
                                  ? 'This room is blocked for maintenance and cannot be assigned. Clear the maintenance request to release it.'
                                  : webCheckInHeld
                                    ? 'This room has an active web check-in invite. Wait until the link expires or cancel the invite before assigning it to a walk-in.'
                                    : room.walk_in_overdue_checkout
                                      ? 'This room still has an overdue checkout. Complete checkout before checking in a new guest here.'
                                      : 'Guest is scheduled to check out today. Complete checkout before assigning this room to a new walk-in.',
                                maintenanceBlocked
                                  ? 'Room blocked'
                                  : webCheckInHeld
                                    ? 'Web check-in hold'
                                    : 'Room not ready',
                                'warning'
                              );
                              return;
                            }
                            if (isWalkIn && isWalkInDirtyRoom(room)) {
                              if (onDirtyRoomHousekeeping) {
                                onDirtyRoomHousekeeping(room);
                              } else {
                                showAlert(
                                  'This room must be cleaned before it can be assigned to a walk-in guest.',
                                  'Room needs cleaning',
                                  'warning'
                                );
                              }
                              return;
                            }
                            if (room.is_available === false) {
                              showAlert(
                                'This room is not available for the selected dates.',
                                'Room unavailable',
                                'warning'
                              );
                              return;
                            }
                            if (!roomTypeGroup) {
                              showAlert('Unable to resolve room type for this room.', 'Error', 'error');
                              return;
                            }

                            const selectedRoomIndex = selectedRooms.findIndex((sr) => sr.roomId === room.id);

                            if (selectedRoomIndex !== -1) {
                              if (onRemoveRoom) {
                                onRemoveRoom(selectedRoomIndex);
                              }
                            } else {
                              const maxAvailable = roomTypeGroup.max_available_rooms || 0;
                              const selectedRoomsOfSameType = selectedRooms.filter(
                                (sr) => sr.roomTypeId === roomTypeGroup.room_type.id
                              ).length;

                              if (maxAvailable > 0 && selectedRoomsOfSameType >= maxAvailable) {
                                const totalRooms = roomTypeGroup.total_rooms || 0;
                                const blockedRooms = roomTypeGroup.blocked_rooms || 0;
                                const bookedRooms = roomTypeGroup.booked_rooms || 0;

                                let limitMessage = `Maximum ${maxAvailable} room(s) available for "${roomTypeGroup.room_type.name}". `;
                                limitMessage += `You have already selected ${selectedRoomsOfSameType} room(s).`;

                                const details = [];
                                details.push(`${totalRooms} total`);
                                if (blockedRooms > 0) details.push(`${blockedRooms} blocked`);
                                if (bookedRooms > 0) details.push(`${bookedRooms} booked`);
                                details.push(`${maxAvailable} available`);

                                limitMessage += `\n\nAvailability: ${details.join(' - ')}`;

                                showAlert(limitMessage, 'Room Not Available', 'warning');
                                return;
                              }

                              const selectedRoomType = allRoomsData?.room_types?.find((rt) => {
                                const rtIdStr = String(rt.room_type.id);
                                const formIdStr = String(roomTypeGroup.room_type.id);
                                return rtIdStr === formIdStr;
                              });
                              const { baseAdults } = resolveRoomTypeLimits(selectedRoomType?.room_type);
                              if (!isWalkIn) {
                                const defaultCheckOut = checkInDate.clone().add(1, 'days');
                                onCheckOutDateChange(defaultCheckOut);
                              }

                              const firstRatePlan = availableMealPlans.length > 0 ? availableMealPlans[0].value : '';

                              onRoomFormDataChange({
                                roomTypeId: roomTypeGroup.room_type.id,
                                roomId: room.id,
                                roomNumber: room.room_number,
                                mealPlan: firstRatePlan,
                                adults: baseAdults,
                                children: 0,
                                ratePerNight: 0,
                                extraAdultCharge: 0,
                                extraChildCharge: 0,
                              });
                              if (onRoomDrawerOpen) {
                                onRoomDrawerOpen();
                              }
                            }
                          }}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>
          );
        })}
      </Box>
    ) : (
      <Alert severity="info">No rooms available</Alert>
    )
  );

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      {isWalkIn && !useWalkInFlowLayout ? (
        <Box sx={{ flex: 1, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <DashboardWorkspaceCard
            themeMode={themeMode}
            title="Select Room"
            subtitle="Choose an available room for walk-in check-in"
            headerActions={walkInHeaderActions}
            minHeight={0}
            maxHeight="100%"
          >
            <Box
              sx={{
                ...getDashboardWorkspaceScrollSx(themeMode),
                p: { xs: 0.75, sm: 1 },
                flex: 1,
                minHeight: 0,
              }}
            >
              {renderRoomGroups()}
            </Box>
          </DashboardWorkspaceCard>
        </Box>
      ) : (
        <Box>
          {isWalkIn ? renderWalkInMobileHeader() : renderReservationHeader()}
          {renderRoomGroups()}
        </Box>
      )}
      {/* Room Details Drawer */}
      <Drawer
        anchor="right"
        open={roomDrawerOpen}
        onClose={onRoomDrawerClose}
        ModalProps={{
          sx: { zIndex: Z_INDEX.DRAWER },
        }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            backgroundColor: roomDrawerSurface,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1 }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              borderBottom: `2px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              backgroundColor: isDark ? '#2a2a31' : '#ffffff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MeetingRoomIcon sx={{ color: CUSTOM_BORDER_COLOR, fontSize: 24 }} />
              <Typography variant="h6" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 600 }}>
                Room Details
              </Typography>
            </Box>
            <IconButton onClick={onRoomDrawerClose} sx={{ color: isDark ? '#999' : '#666' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Scrollable Content */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
            {drawerValidationError && (
              <Alert
                severity="warning"
                onClose={() => setDrawerValidationError('')}
                sx={{ mb: 2 }}
              >
                {drawerValidationError}
              </Alert>
            )}
            {/* Selected Room Type + Room hero card */}
            {(roomFormData.roomTypeId || roomFormData.roomId) && (
              <Box
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                  minHeight: 120,
                  height: 120,
                  display: 'flex',
                  flexDirection: 'row',
                  boxShadow: isDark
                    ? '0 10px 24px rgba(0,0,0,0.32)'
                    : '0 8px 20px rgba(0,0,0,0.12)',
                }}
              >
                <Box
                  sx={{
                    width: '40%',
                    minHeight: 120,
                    position: 'relative',
                    borderRight: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                  }}
                >
                  {selectedRoomTypeImage ? (
                    <Box
                      component="img"
                      src={selectedRoomTypeImage}
                      alt={selectedRoomTypeName}
                      sx={{
                        width: '100%',
                        height: '100%',
                        minHeight: 120,
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        minHeight: 120,
                        background: isDark
                          ? 'linear-gradient(135deg, #25313d 0%, #1f2a38 45%, #18202c 100%)'
                          : 'linear-gradient(135deg, #eafaf5 0%, #d6f5ea 45%, #c6f0e2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <HotelIcon sx={{ fontSize: 54, color: `${CUSTOM_BORDER_COLOR}AA` }} />
                    </Box>
                  )}

                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      left: 10,
                      right: 10,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Chip
                      size="small"
                      icon={<HotelIcon sx={{ color: '#0d1f1a !important' }} />}
                      label={selectedRoomTypeName}
                      sx={{
                        maxWidth: '100%',
                        height: 24,
                        backgroundColor: `${CUSTOM_BORDER_COLOR}ee`,
                        color: '#0d1f1a',
                        fontWeight: 700,
                        '.MuiChip-label': {
                          px: 0.75,
                          fontSize: '0.68rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    width: '60%',
                    p: 1.25,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    background: isDark
                      ? 'linear-gradient(180deg, #2b3140 0%, #252b38 100%)'
                      : 'linear-gradient(180deg, #f8fafc 0%, #eef4f8 100%)',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      mb: 0.35,
                    }}
                  >
                    <MeetingRoomIcon sx={{ color: CUSTOM_BORDER_COLOR, fontSize: 18 }} />
                    <Typography
                      variant="caption"
                      sx={{
                        color: isDark ? '#a7b0bf' : '#607080',
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        fontWeight: 700,
                      }}
                    >
                      Booking Selection
                    </Typography>
                  </Box>
                  <Typography
                    variant="subtitle2"
                    noWrap
                    sx={{
                      color: isDark ? '#f4f6f8' : '#1b2733',
                      fontWeight: 700,
                      lineHeight: 1.2,
                    }}
                  >
                    {selectedRoomTypeName}
                  </Typography>
                  {selectedRoomNumberLabel && (
                    <Chip
                      size="small"
                      icon={<MeetingRoomIcon sx={{ color: '#111 !important' }} />}
                      label={`Room ${selectedRoomNumberLabel}`}
                      sx={{
                        mt: 0.7,
                        width: 'fit-content',
                        height: 24,
                        backgroundColor: `${CUSTOM_BORDER_COLOR}`,
                        color: '#111',
                        fontWeight: 700,
                        '.MuiChip-label': { px: 0.85, fontSize: '0.72rem' },
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      mt: 0.8,
                      p: 0.75,
                      borderRadius: 1,
                      border: `1px solid ${isDark ? '#3a4252' : '#d5e2ea'}`,
                      backgroundColor: isDark ? '#232a36' : '#ffffff',
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 0.5,
                    }}
                  >
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.1 }}>
                        <MeetingRoomIcon sx={{ fontSize: 12, color: isDark ? '#8fa0b1' : '#6b7f90' }} />
                        <Typography variant="caption" sx={{ color: isDark ? '#8fa0b1' : '#6b7f90', fontWeight: 600, lineHeight: 1.1 }}>
                          Room No
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: isDark ? '#f2f5f8' : '#1e2f3d', fontWeight: 700 }}>
                        {selectedRoomNumberLabel || '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.1 }}>
                        <RestaurantMenuIcon sx={{ fontSize: 12, color: isDark ? '#8fa0b1' : '#6b7f90' }} />
                        <Typography variant="caption" sx={{ color: isDark ? '#8fa0b1' : '#6b7f90', fontWeight: 600, lineHeight: 1.1 }}>
                          Meal Plan
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: isDark ? '#f2f5f8' : '#1e2f3d', fontWeight: 700 }}>
                        {(roomFormData.mealPlan || 'Not set').toString()}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Meal Plan - Moved before Rate Information */}
            {roomFormData.roomTypeId && (
              <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel sx={{ color: isDark ? '#999' : '#666' }}>Meal Plan</InputLabel>
              <Select
                value={roomFormData.mealPlan || (availableMealPlans.length > 0 ? availableMealPlans[0].value : '')}
                onChange={(e) => onRoomFormDataChange({ ...roomFormData, mealPlan: e.target.value })}
                label="Meal Plan"
                disabled={availableMealPlans.length === 0}
                MenuProps={mealPlanMenuProps}
                sx={{
                  color: isDark ? '#ddd' : '#333',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: CUSTOM_BORDER_COLOR,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: CUSTOM_BORDER_COLOR,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: CUSTOM_BORDER_COLOR,
                  },
                }}
              >
                {availableMealPlans.length > 0 ? (
                  availableMealPlans.map((plan) => (
                    <MenuItem key={plan.value || plan.id} value={plan.value}>
                      {plan.label}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled value="">
                    No meal plans available
                  </MenuItem>
                )}
              </Select>
              {availableMealPlans.length === 0 && (
                <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
                  No rate plans configured for this hotel
                </Typography>
              )}
            </FormControl>
            )}

            {/* Rate Display - Show when room type and check-in date are selected */}
            {selectedRoomType && checkInDate && (
              <Box sx={{ mb: 3, p: 2, border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`, borderRadius: 1, backgroundColor: isDark ? '#2a2a31' : '#f8f9fa' }}>
                <Typography variant="body2" gutterBottom sx={{ color: isDark ? '#999' : '#666', mb: 1, fontWeight: 500 }}>
                  Rate Information
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', mb: 2, display: 'block' }}>
                  For {formatHotelDate(checkInDate)}
                  {roomFormData.mealPlan && ` - Rate Plan: ${availableMealPlans.find(p => p.value === roomFormData.mealPlan)?.label || roomFormData.mealPlan}`}
                </Typography>
                {roomFormData.mealPlan && !mealPlanRates && (
                  <Typography variant="caption" sx={{ color: '#ff9800', mb: 1, display: 'block' }}>
                    Loading rates...
                  </Typography>
                )}
                {roomFormData.mealPlan && mealPlanRates && Object.keys(mealPlanRates).length === 0 && (
                  <Typography variant="caption" sx={{ color: '#f44336', mb: 1, display: 'block' }}>
                    No rates found for this date and rate plan
                  </Typography>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Single Person Rate */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: 32, 
                      height: 32, 
                      borderRadius: '50%', 
                      backgroundColor: CUSTOM_BORDER_COLOR,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#000',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                    }}>
                      1
                    </Box>
                    <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333' }}>
                      x 1 (Single)
                    </Typography>
                    <Typography variant="body1" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 500, ml: 'auto' }}>
                      {singlePersonRate > 0 ? formatCurrency(singlePersonRate) : 'N/A'}
                    </Typography>
                  </Box>
                  
                  {/* Base Rate (base_adults persons) */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: 32, 
                      height: 32, 
                      borderRadius: '50%', 
                      backgroundColor: CUSTOM_BORDER_COLOR,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#000',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                    }}>
                      {baseAdults}
                    </Box>
                    <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333' }}>
                      x {baseAdults} (Base)
                    </Typography>
                    <Typography variant="body1" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 500, ml: 'auto' }}>
                      {baseRate > 0 ? formatCurrency(baseRate) : 'N/A'}
                    </Typography>
                  </Box>
                  
                  {/* Extra Adult Rate (if available) */}
                  {extraAdultRate > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 4 }}>
                      <Typography variant="body2" sx={{ color: isDark ? '#999' : '#666' }}>
                        Extra Adult
                      </Typography>
                      <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333', ml: 'auto' }}>
                        {formatCurrency(extraAdultRate)} /person
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Extra Child Rate (if available) */}
                  {extraChildRate > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 4 }}>
                      <Typography variant="body2" sx={{ color: isDark ? '#999' : '#666' }}>
                        Extra Child
                      </Typography>
                      <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333', ml: 'auto' }}>
                        {formatCurrency(extraChildRate)} /person
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

          {/* Adults Selection */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333' }}>
                Adults {`(Max: ${maxTotalAdults})`}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  color="primary"
                  onClick={() => handleNumberChange('adults', -1)}
                  disabled={!roomFormData.roomTypeId || roomFormData.adults <= 0}
                  sx={{
                    border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                    '&:hover': {
                      border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                      backgroundColor: `${CUSTOM_BORDER_COLOR}20`,
                    },
                    '&.Mui-disabled': {
                      border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                    },
                  }}
                >
                  <RemoveIcon />
                </IconButton>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    minWidth: 60,
                    textAlign: 'center',
                    color: isDark ? '#ddd' : '#333',
                    fontWeight: 500,
                  }}
                >
                  {roomFormData.adults || 0}
                </Typography>
                <IconButton
                  color="primary"
                  onClick={() => handleNumberChange('adults', 1)}
                  disabled={!roomFormData.roomTypeId || !selectedRoomType || (roomFormData.adults || 0) >= (isNaN(maxTotalAdults) ? maxGuests : maxTotalAdults)}
                  sx={{
                    border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                    '&:hover': {
                      border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                      backgroundColor: `${CUSTOM_BORDER_COLOR}20`,
                    },
                    '&.Mui-disabled': {
                      border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                    },
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>
            {(roomFormData.adults + roomFormData.children) > maxTotalCapacity && (
              <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
                Total guests ({roomFormData.adults + roomFormData.children}) exceeds capacity ({maxTotalCapacity})
              </Typography>
            )}
          </Box>

          {/* Children Selection */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333' }}>
                Children {selectedRoomType && `(Max: ${isNaN(maxTotalChildren) ? maxGuests : maxTotalChildren})`}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  color="primary"
                  onClick={() => handleNumberChange('children', -1)}
                  disabled={!roomFormData.roomTypeId || !selectedRoomType || (roomFormData.children || 0) <= 0}
                  sx={{
                    border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                    '&:hover': {
                      border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                      backgroundColor: `${CUSTOM_BORDER_COLOR}20`,
                    },
                    '&.Mui-disabled': {
                      border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                    },
                  }}
                >
                  <RemoveIcon />
                </IconButton>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    minWidth: 60,
                    textAlign: 'center',
                    color: isDark ? '#ddd' : '#333',
                    fontWeight: 500,
                  }}
                >
                  {roomFormData.children || 0}
                </Typography>
                <IconButton
                  color="primary"
                  onClick={() => handleNumberChange('children', 1)}
                  disabled={!roomFormData.roomTypeId || !selectedRoomType || (roomFormData.children || 0) >= (isNaN(maxTotalChildren) ? maxGuests : maxTotalChildren)}
                  sx={{
                    border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                    '&:hover': {
                      border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                      backgroundColor: `${CUSTOM_BORDER_COLOR}20`,
                    },
                    '&.Mui-disabled': {
                      border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                    },
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>

          {/* Rate Per Night */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Rate Per Night"
              type="number"
              value={roomFormData.ratePerNight || 0}
              onChange={(e) => onRoomFormDataChange({ ...roomFormData, ratePerNight: parseFloat(e.target.value) || 0 })}
              inputProps={{ step: 0.01, min: 0 }}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: CUSTOM_BORDER_COLOR,
                  },
                  '&:hover fieldset': {
                    borderColor: CUSTOM_BORDER_COLOR,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: CUSTOM_BORDER_COLOR,
                  },
                },
              }}
            />
          </Box>

          {/* Extra Adult Charge Input - Show when adults exceed base capacity */}
          {extraAdultsCount > 0 && (
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Extra Adult Charge/Night"
                type="number"
                value={roomFormData.extraAdultCharge || extraAdultRate || 0}
                onChange={(e) => {
                  userEditedAdultCharge.current = true; // Mark as manually edited
                  onRoomFormDataChange({ ...roomFormData, extraAdultCharge: parseFloat(e.target.value) || 0 });
                }}
                inputProps={{ step: 0.01, min: 0 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: CUSTOM_BORDER_COLOR,
                    },
                    '&:hover fieldset': {
                      borderColor: CUSTOM_BORDER_COLOR,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: CUSTOM_BORDER_COLOR,
                    },
                  },
                }}
              />
            </Box>
          )}

          {/* Extra Child Charge Input - Show when children exceed base capacity */}
          {extraChildrenCount > 0 && (
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Extra Child Charge/Night"
                type="number"
                value={roomFormData.extraChildCharge || extraChildRate || 0}
                onChange={(e) => {
                  userEditedChildCharge.current = true; // Mark as manually edited
                  onRoomFormDataChange({ ...roomFormData, extraChildCharge: parseFloat(e.target.value) || 0 });
                }}
                inputProps={{ step: 0.01, min: 0 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: CUSTOM_BORDER_COLOR,
                    },
                    '&:hover fieldset': {
                      borderColor: CUSTOM_BORDER_COLOR,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: CUSTOM_BORDER_COLOR,
                    },
                  },
                }}
              />
            </Box>
          )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 1.5,
              borderTop: `2px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              backgroundColor: isDark ? '#2a2a31' : '#ffffff',
              display: 'flex',
              gap: 1.5,
            }}
          >
            <Button
              variant="outlined"
              fullWidth
              onClick={onRoomDrawerClose}
              sx={{
                borderColor: isDark ? '#333246' : '#e0e0e0',
                color: isDark ? '#ddd' : '#666',
                '&:hover': {
                  borderColor: CUSTOM_BORDER_COLOR,
                  backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                  color: CUSTOM_BORDER_COLOR,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleAddRoom}
              disabled={!roomFormData.roomTypeId || !roomFormData.roomId}
              sx={{
                backgroundColor: CUSTOM_BORDER_COLOR,
                color: '#000',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: CUSTOM_BORDER_COLOR,
                  opacity: 0.9,
                  boxShadow: `0 4px 12px ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                },
                '&:disabled': {
                  backgroundColor: isDark ? '#333246' : '#e0e0e0',
                  color: isDark ? '#999' : '#666',
                },
              }}
            >
              Add Room
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Alert Dialog */}
      <AlertDialog
        open={alertDialogOpen}
        onClose={handleCloseAlert}
        title={alertTitle}
        message={alertMessage}
        severity={alertSeverity}
      />
    </LocalizationProvider>
  );
};

export default RoomsStep;

